import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "../components/Icon.js";
import { ThemeToggle } from "../components/ThemeToggle.js";
import { useAuth } from "../auth/AuthContext.js";
import { ApiError } from "../api/client.js";

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fromState = location.state as LocationState | null;
  const dest = fromState?.from && fromState.from !== "/login" ? fromState.from : "/";

  if (!loading && user) return <Navigate to={dest} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate(dest, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Email or password is incorrect.");
      } else {
        setError(err instanceof Error ? err.message : "Sign-in failed.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-radial-deep min-h-screen flex flex-col items-center justify-center selection:bg-primary-container selection:text-on-primary-container relative">
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      <main className="w-full max-w-md px-6 py-12 flex-grow flex items-center justify-center">
        <div className="glass-card-elevated w-full rounded-xl p-8 space-y-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary-container to-transparent opacity-50" />
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center space-x-2 text-primary-container mb-2">
              <Icon name="sync" className="text-3xl" />
              <h1 className="text-xl font-black tracking-tighter uppercase">AppsDoCalSync</h1>
            </div>
            <h2 className="text-headline-md text-on-surface tracking-tight">Sign in</h2>
            <p className="text-body-sm text-on-surface-variant max-w-[260px] mx-auto">
              Use the email + password your admin gave you, or accept an invitation link.
            </p>
          </div>
          <form onSubmit={onSubmit} className="space-y-5">
            <Field
              id="email"
              label="Email"
              icon="mail"
              type="email"
              placeholder="you@appsdo.in"
              value={email}
              onChange={setEmail}
              required
              autoComplete="email"
            />
            <Field
              id="password"
              label="Password"
              icon="lock"
              type="password"
              placeholder="••••••••••••"
              value={password}
              onChange={setPassword}
              required
              autoComplete="current-password"
            />
            {error && (
              <div className="bg-error-container/20 border border-error/30 text-error rounded-lg p-3 text-body-sm flex items-start gap-2">
                <Icon name="error" className="text-[18px] mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary-container text-on-primary-container text-body-base font-semibold py-3 rounded-lg hover:brightness-110 active:scale-[0.98] transition-all flex items-center justify-center space-x-2 shadow-lg shadow-primary-container/10 disabled:opacity-60 disabled:cursor-progress"
              >
                <span>{submitting ? "Signing in…" : "Sign in"}</span>
                <Icon name="login" className="text-[18px]" />
              </button>
            </div>
          </form>
          <div className="pt-4 border-t border-outline-variant/30 text-center">
            <div className="flex items-center justify-center gap-md">
              <Indicator label="All access logged" tone="warn" />
              <Indicator label="Hub live" tone="ok" pulse />
            </div>
          </div>
        </div>
      </main>
      <div className="fixed -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-30 pointer-events-none">
        <div className="absolute top-[10%] left-[20%] w-[300px] h-[300px] bg-primary-container/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[10%] right-[20%] w-[400px] h-[400px] bg-secondary-container/10 rounded-full blur-[150px]" />
      </div>
    </div>
  );
}

function Field({
  id,
  label,
  icon,
  type,
  placeholder,
  value,
  onChange,
  required,
  autoComplete,
}: {
  id: string;
  label: string;
  icon: string;
  type: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  autoComplete?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-label-xs text-outline uppercase tracking-widest font-mono px-1">
        {label}
      </label>
      <div className="relative group">
        <Icon
          name={icon}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary-container transition-colors"
        />
        <input
          id={id}
          name={id}
          type={type}
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className="w-full bg-surface-container-lowest border border-outline-variant rounded-lg py-2.5 pl-10 pr-4 text-on-surface text-body-sm placeholder:text-outline/50 focus:outline-none focus:border-primary-container input-glow transition-all"
        />
      </div>
    </div>
  );
}

function Indicator({ label, tone, pulse }: { label: string; tone: "ok" | "warn"; pulse?: boolean }) {
  const dot = tone === "ok" ? "bg-primary-fixed-dim" : "bg-tertiary-container";
  return (
    <div className="flex items-center gap-1.5 text-on-surface-variant">
      <span className={`w-1.5 h-1.5 ${dot} rounded-full ${pulse ? "animate-pulse" : ""}`} />
      <span className="font-mono text-label-xs uppercase">{label}</span>
    </div>
  );
}
